// HomeDeliveryModal.tsx
import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Grid,
  Alert,
  IconButton,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Switch,
  FormControlLabel,
  CircularProgress
} from '@mui/material';
import {
  Close as CloseIcon,
  LocalShipping as ShippingIcon,
  Home as HomeIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Print as PrintIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from '../services/api';

interface HomeDeliveryModalProps {
  open: boolean;
  onClose: () => void;
  order: any;
  onDeliveryPrepared: (data: any) => void;
}

interface HsinchuLabelData {
  trackingNumber?: string;
  recipientName?: string;
  recipientAddress?: string;
  weight?: number;
  [key: string]: any;
}

const HomeDeliveryModal: React.FC<HomeDeliveryModalProps> = ({
  open,
  onClose,
  order,
  onDeliveryPrepared
}) => {
  const { enqueueSnackbar } = useSnackbar();
  
  // Form state
  const [loading, setLoading] = useState(false);
  const [useHsinchu, setUseHsinchu] = useState(true);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [carrier, setCarrier] = useState('新竹物流');
  const [deliveryNotes, setDeliveryNotes] = useState('');
  const [estimatedDeliveryDate, setEstimatedDeliveryDate] = useState('');
  const [weight, setWeight] = useState('1');
  const [declaredValue, setDeclaredValue] = useState('');
  const [productType, setProductType] = useState('001');
  const [recipientPhone, setRecipientPhone] = useState(''); // Add phone state
  
  // Hsinchu response data
  const [hsinchuResult, setHsinchuResult] = useState<any>(null);

  // Carrier options
  const carrierOptions = [
    { value: '新竹物流', label: '新竹物流' },
    { value: '黑貓宅急便', label: '黑貓宅急便' },
    { value: '郵局', label: '中華郵政' },
    { value: '嘉里大榮', label: '嘉里大榮' },
    { value: '宅配通', label: '宅配通' },
    { value: '其他', label: '其他' }
  ];

  // Product type options for Hsinchu
  const productTypeOptions = [
    { value: '001', label: '一般商品' },
    { value: '003', label: '冷凍商品' },
    { value: '008', label: '冷藏商品' }
  ];

  const formatNameForHsinchu = (name: string): string => {
  if (!name || name === '客戶') return name;
  
  // Split by space and reverse if there are exactly 2 parts
  const nameParts = name.trim().split(/\s+/);
  if (nameParts.length === 2) {
    return `${nameParts[1]}${nameParts[0]}`;
  }
  return name;
};

// Add these helper functions
const formatPhoneForHsinchu = (phone: string): string => {
  if (!phone) return '';
  // Remove +886 and replace with 0, also remove any spaces or dashes
  return phone.replace(/^\+886/, '0').replace(/[\s-]/g, '');
};

const formatAddressForHsinchu = (address: string): string => {
  if (!address) return '';
  // Remove "Taiwan" and clean up extra commas/spaces
  return address.replace(/, Taiwan,?/gi, '').replace(/,\s*$/, '').trim();
};

const hexToBase64 = (hexString: string): string => {
  const bytes = [];
  for (let i = 0; i < hexString.length; i += 2) {
    bytes.push(parseInt(hexString.substr(i, 2), 16));
  }
  
  const uint8Array = new Uint8Array(bytes);
  let binaryString = '';
  for (let i = 0; i < uint8Array.length; i++) {
    binaryString += String.fromCharCode(uint8Array[i]);
  }
  
  return btoa(binaryString);
};

const displayShippingLabel = (hexImage: string, labelData: HsinchuLabelData = {}) => {
  if (!hexImage) {
    console.error('No image data provided');
    return;
  }

  const base64Image = hexToBase64(hexImage);
  const newWindow = window.open('', '_blank');
  
  if (!newWindow) {
    alert('Please allow popups to view the shipping label');
    return;
  }

  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>宅配出貨單 - ${labelData.trackingNumber || '追蹤號碼'}</title>
      <style>
        body {
          margin: 0; padding: 20px; font-family: Arial, sans-serif;
          background-color: #f5f5f5; display: flex; flex-direction: column; align-items: center;
        }
        .header {
          text-align: center; margin-bottom: 20px; background: white;
          padding: 15px; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          width: 100%; max-width: 800px;
        }
        .header h1 { color: #2c3e50; margin: 0 0 10px 0; }
        .tracking-info {
          display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 10px; margin-top: 15px; text-align: left;
        }
        .info-item {
          background: #f8f9fa; padding: 8px 12px; border-radius: 4px;
          border-left: 3px solid #3498db;
        }
        .info-label { font-weight: bold; color: #2c3e50; font-size: 0.9em; }
        .info-value { color: #34495e; margin-top: 2px; }
        .label-container {
          background: white; padding: 20px; border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1); max-width: 100%; overflow: auto;
        }
        .shipping-label {
          max-width: 100%; height: auto; border: 1px solid #ddd; border-radius: 4px;
        }
        .actions { margin-top: 20px; text-align: center; }
        .btn {
          background: #3498db; color: white; border: none; padding: 10px 20px;
          border-radius: 4px; cursor: pointer; font-size: 14px; margin: 0 5px;
          transition: background-color 0.3s;
        }
        .btn:hover { background: #2980b9; }
        .btn-print { background: #27ae60; }
        .btn-print:hover { background: #229954; }
        .btn-download { background: #e74c3c; }
        .btn-download:hover { background: #c0392b; }
        @media print {
          body { background: white; padding: 0; }
          .header, .actions { display: none; }
          .label-container { box-shadow: none; padding: 0; margin: 0; }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>🚚 宅配出貨單</h1>
        <p>新竹物流 Hsinchu Logistics</p>
        ${labelData.trackingNumber ? `
        <div class="tracking-info">
          <div class="info-item">
            <div class="info-label">追蹤號碼</div>
            <div class="info-value">${labelData.trackingNumber}</div>
          </div>
          ${labelData.recipientName ? `
          <div class="info-item">
            <div class="info-label">收件人</div>
            <div class="info-value">${labelData.recipientName}</div>
          </div>` : ''}
          ${labelData.recipientAddress ? `
          <div class="info-item">
            <div class="info-label">收件地址</div>
            <div class="info-value">${labelData.recipientAddress}</div>
          </div>` : ''}
          ${labelData.weight ? `
          <div class="info-item">
            <div class="info-label">重量</div>
            <div class="info-value">${labelData.weight} kg</div>
          </div>` : ''}
        </div>` : ''}
      </div>
      <div class="label-container">
        <img src="data:image/png;base64,${base64Image}" 
             alt="Shipping Label" class="shipping-label"
             onload="console.log('Label loaded')" onerror="console.error('Label failed')">
      </div>
      <div class="actions">
        <button class="btn btn-print" onclick="window.print()">🖨️ 列印</button>
        <button class="btn btn-download" onclick="
          const link = document.createElement('a');
          link.href = 'data:image/png;base64,${base64Image}';
          link.download = '宅配出貨單_${labelData.trackingNumber || Date.now()}.png';
          link.click();
        ">💾 下載</button>
        <button class="btn" onclick="window.close()">❌ 關閉</button>
      </div>
    </body>
    </html>
  `;

  newWindow.document.write(htmlContent);
  newWindow.document.close();
};

  const handleSubmit = useCallback(async () => {
  // Validate based on selected mode
  if (useHsinchu && carrier === '新竹物流') {
    if (!order.shippingAddress || !weight) {
      enqueueSnackbar('請確認收貨地址和重量', { variant: 'warning' });
      return;
    }
  } else {
    if (!trackingNumber.trim()) {
      enqueueSnackbar('請輸入追蹤號碼', { variant: 'warning' });
      return;
    }
  }

  setLoading(true);
  try {
    // Declare result with proper type annotation
    let result: any = null;

    if (useHsinchu && carrier === '新竹物流') {
      // Use Hsinchu Logistics API
      console.log('Creating Hsinchu shipping order...');
      
      const hsinchuResponse = await api.post('/api/hsinchu/create-order', {
        orderId: order.orderId.toString(),
        recipientName: formatNameForHsinchu(order.customer),
        recipientPhone: formatPhoneForHsinchu(recipientPhone || order.customerPhone),
        recipientAddress: formatAddressForHsinchu(order.shippingAddress),
        weight: 60, // Always set to 60 for 總長
        itemCount: 1, 
        notes: deliveryNotes,
        declaredValue: declaredValue ? parseFloat(declaredValue) : 0,
        productType: productType
      });

      if (hsinchuResponse.data.success) {
        result = hsinchuResponse.data.data;
        setHsinchuResult(result);
        
        // Display the shipping label immediately after creation
        if (result.labelImage) {
          displayShippingLabel(result.labelImage, {
            trackingNumber: result.trackingNumber,
            recipientName: order.customer,
            recipientAddress: order.shippingAddress,
            weight: result.weight || 60
          });
        }
        
        enqueueSnackbar('新竹物流出貨單已建立成功！', { variant: 'success' });
        
        // Update tracking info for all items in the order
        const trackingPromises = order.items.map((item: any) => {
          return api.post('/api/orders/shopify-tracking', {
            orderId: order.orderId.toString(),
            productName: item.productName,
            variantTitle: item.itemSpec,
            trackingNumber: result.trackingNumber,
            deliveryMethod: 'home_delivery',
            carrier: '新竹物流',
            userId: JSON.parse(localStorage.getItem('currentUser') || '{}').id,
            userName: JSON.parse(localStorage.getItem('currentUser') || '{}').name,
            timestamp: new Date().toISOString()
          });
        });

        await Promise.all(trackingPromises);

        // Call the callback with delivery data
        onDeliveryPrepared({
          orderId: order.orderId,
          trackingNumber: result.trackingNumber,
          carrier: '新竹物流',
          deliveryMethod: 'home_delivery',
          weight: weight,
          itemCount: order.items?.length || 1,
          notes: deliveryNotes,
          estimatedDeliveryDate: estimatedDeliveryDate || null,
          hsinchuData: result,
          hasTrackingInfo: true
        });

      } else {
        throw new Error(hsinchuResponse.data.error || '建立新竹物流出貨單失敗');
      }

    } else {
      // Manual tracking number entry (existing logic)
      const deliveryData = {
        orderId: order.orderId.toString(),
        platform: 'shopify',
        deliveryMethod: 'home_delivery',
        trackingNumber: trackingNumber.trim(),
        carrier: carrier,
        deliveryNotes: deliveryNotes,
        estimatedDeliveryDate: estimatedDeliveryDate || null,
        weight: weight,
        createdAt: new Date().toISOString()
      };

      // Save home delivery information to your existing endpoint
      const response = await api.post('/api/orders/home-delivery', deliveryData);
      
      if (response.data?.success) {
        // Update tracking info for all items in the order
        const trackingPromises = order.items.map((item: any) => {
          return api.post('/api/orders/shopify-tracking', {
            orderId: order.orderId.toString(),
            productName: item.productName,
            variantTitle: item.itemSpec,
            trackingNumber: trackingNumber.trim(),
            deliveryMethod: 'home_delivery',
            carrier: carrier,
            userId: JSON.parse(localStorage.getItem('currentUser') || '{}').id,
            userName: JSON.parse(localStorage.getItem('currentUser') || '{}').name,
            timestamp: new Date().toISOString()
          });
        });

        await Promise.all(trackingPromises);

        enqueueSnackbar('宅配資訊已成功建立', { variant: 'success' });
        
        // Call the callback with delivery data
        onDeliveryPrepared({
          ...deliveryData,
          hasTrackingInfo: true
        });

      } else {
        throw new Error(response.data?.message || '建立宅配資訊失敗');
      }
    }
    
    onClose();
    
  } catch (error: any) {
    console.error('Home delivery setup error:', error);
    enqueueSnackbar(`建立宅配資訊失敗: ${error.message}`, { variant: 'error' });
  } finally {
    setLoading(false);
  }
}, [order, trackingNumber, carrier, deliveryNotes, estimatedDeliveryDate, weight, declaredValue, productType, useHsinchu, recipientPhone, onDeliveryPrepared, onClose, enqueueSnackbar]);

  const handleClose = useCallback(() => {
    if (!loading) {
      setHsinchuResult(null);
      onClose();
    }
  }, [loading, onClose]);

  const handlePrintLabel = useCallback(async () => {
  if (!hsinchuResult) {
    enqueueSnackbar('無出貨單資料', { variant: 'warning' });
    return;
  }
  
  try {
    enqueueSnackbar('正在開啟出貨標籤...', { variant: 'info', autoHideDuration: 2000 });
    
    // If we already have the label image, display it directly
    if (hsinchuResult.labelImage) {
      displayShippingLabel(hsinchuResult.labelImage, {
        trackingNumber: hsinchuResult.trackingNumber || '待產生',
        recipientName: order?.customer || '',
        recipientAddress: order?.shippingAddress || '',
        weight: hsinchuResult.weight || 60
      });
      
      enqueueSnackbar('標籤已在新視窗開啟', { variant: 'success' });
      return;
    }
    
    // If we have a tracking number, try to fetch the label
    if (hsinchuResult.trackingNumber) {
      const response = await api.get(`/api/hsinchu/label/${hsinchuResult.trackingNumber}`);
      
      if (response.data.success && response.data.data?.labelImage) {
        displayShippingLabel(response.data.data.labelImage, {
          trackingNumber: hsinchuResult.trackingNumber,
          recipientName: order?.customer || '',
          recipientAddress: order?.shippingAddress || '',
          weight: hsinchuResult.weight || 60
        });
        
        enqueueSnackbar('標籤已在新視窗開啟', { variant: 'success' });
      } else {
        throw new Error('無法獲取標籤圖片');
      }
    } else {
      // No tracking number available
      enqueueSnackbar('追蹤號碼尚未產生，無法顯示標籤', { variant: 'warning' });
    }
  } catch (error: any) {
    console.error('Error displaying label:', error);
    enqueueSnackbar(`無法顯示標籤: ${error.message}`, { variant: 'error' });
  }
}, [hsinchuResult, order, enqueueSnackbar]);

  // Reset form when modal opens
  React.useEffect(() => {
    if (open) {
      setTrackingNumber('');
      setCarrier('新竹物流');
      setDeliveryNotes('');
      setEstimatedDeliveryDate('');
      setWeight('1');
      setDeclaredValue('');
      setProductType('001');
      setUseHsinchu(true);
      setHsinchuResult(null);
      // Set phone from order data or empty
      setRecipientPhone(order?.customerPhone || '');
    }
  }, [open, order?.customerPhone]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '700px' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HomeIcon color="warning" />
          <Typography variant="h6">
            宅配出貨設定 - 訂單 #{order?.orderNumber}
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
          disabled={loading}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      {/* Always show print button, but with different text based on tracking status */}
      <Button
        variant="contained"
        startIcon={<PrintIcon />}
        onClick={handlePrintLabel}
        size="small"
        color={hsinchuResult?.trackingNumber ? "primary" : "warning"}
      >
        {hsinchuResult?.trackingNumber ? '列印出貨標籤' : '檢視出貨資訊'}
      </Button>
      
      {hsinchuResult?.labelImage && (
        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          onClick={handlePrintLabel}
          size="small"
          color="secondary"
        >
          重新顯示標籤
        </Button>
      )}
      
      {/* Show copy button only if tracking number exists */}
      {hsinchuResult?.trackingNumber && (
        <Button
          variant="text"
          size="small"
          onClick={() => {
            navigator.clipboard.writeText(hsinchuResult.trackingNumber);
            enqueueSnackbar('追蹤號碼已複製', { variant: 'success' });
          }}
        >
          複製追蹤號碼
        </Button>
      )}
      
      {/* Show refresh button if no tracking number */}
      {!hsinchuResult?.trackingNumber && (
        <Button
          variant="outlined"
          size="small"
          onClick={async () => {
            // Try to refresh the order status
            try {
              const response = await api.get(`/api/hsinchu/query-tracking/${order.orderId}`);
              if (response.data.success && response.data.data.trackingNumber) {
                setHsinchuResult({
                  ...hsinchuResult,
                  trackingNumber: response.data.data.trackingNumber
                });
                enqueueSnackbar('追蹤號碼已更新', { variant: 'success' });
              }
            } catch (error) {
              enqueueSnackbar('無法獲取最新狀態', { variant: 'warning' });
            }
          }}
        >
          刷新狀態
        </Button>
      )}
    </Box>

        {!hsinchuResult && (
          <>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                設定宅配出貨資訊。可選擇使用新竹物流自動建立出貨單，或手動輸入其他物流商的追蹤號碼。
              </Typography>
            </Alert>

            {/* Mode Selection */}
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={useHsinchu}
                    onChange={(e) => setUseHsinchu(e.target.checked)}
                    disabled={loading}
                  />
                }
                label={
                  <Box>
                    <Typography variant="subtitle1">
                      使用新竹物流 API 自動建立出貨單
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {useHsinchu ? '將自動生成追蹤號碼和出貨標籤' : '手動輸入追蹤號碼'}
                    </Typography>
                  </Box>
                }
              />
            </Paper>


            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<PrintIcon />}
                onClick={handlePrintLabel}
                size="small"
                color="primary"
              >
                列印出貨標籤
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={handlePrintLabel}
                size="small"
                color="secondary"
              >
                重新顯示標籤
              </Button>
              
              {hsinchuResult?.trackingNumber && (
                <Button
                  variant="text"
                  size="small"
                  onClick={() => {
                    navigator.clipboard.writeText(hsinchuResult.trackingNumber);
                    enqueueSnackbar('追蹤號碼已複製', { variant: 'success' });
                  }}
                >
                  複製追蹤號碼
                </Button>
              )}
            </Box>

            {/* Order Information */}
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon />
                訂單資訊
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">客戶</Typography>
                  <Typography variant="body1">{order?.customer}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">聯絡電話</Typography>
                  <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PhoneIcon fontSize="small" />
                    {order?.customerPhone || '未提供'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationIcon fontSize="small" />
                    配送地址
                  </Typography>
                  <Typography variant="body1">{order?.shippingAddress}</Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* Items List */}
            <Paper variant="outlined" sx={{ mb: 3 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>商品名稱</TableCell>
                      <TableCell>規格</TableCell>
                      <TableCell align="center">數量</TableCell>
                      <TableCell align="center">已收貨</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {order?.items?.map((item: any, index: number) => (
                      <TableRow key={index}>
                        <TableCell>{item.productName}</TableCell>
                        <TableCell>{item.itemSpec}</TableCell>
                        <TableCell align="center">{item.quantity}</TableCell>
                        <TableCell align="center">
                          {item.received ? (
                            <Chip label="✓" color="success" size="small" />
                          ) : (
                            <Chip label="✗" color="error" size="small" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            <Divider sx={{ my: 3 }} />

            {/* Delivery Settings */}
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShippingIcon />
              宅配設定
            </Typography>

            <Grid container spacing={3}>
              {/* Carrier Selection */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>物流商</InputLabel>
                  <Select
                    value={carrier}
                    label="物流商"
                    onChange={(e) => setCarrier(e.target.value)}
                    disabled={loading}
                  >
                    {carrierOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                        {option.value === '新竹物流' && useHsinchu && (
                          <Chip 
                            label="API整合" 
                            size="small" 
                            color="primary" 
                            sx={{ ml: 1 }} 
                          />
                        )}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Weight (always shown for Hsinchu) */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required={useHsinchu && carrier === '新竹物流'}
                  label="重量 (kg)"
                  type="number"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  disabled={loading}
                  inputProps={{ min: 0.1, step: 0.1 }}
                  helperText="用於計算運費和產生標籤"
                />
              </Grid>

              {/* Hsinchu-specific fields */}
              {useHsinchu && carrier === '新竹物流' && (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      label="收貨人電話"
                      value={recipientPhone}
                      onChange={(e) => setRecipientPhone(e.target.value)}
                      disabled={loading}
                      placeholder={order?.customerPhone || '請輸入收貨人電話'}
                      helperText="新竹物流必填欄位"
                      InputProps={{
                        startAdornment: <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>商品類型</InputLabel>
                      <Select
                        value={productType}
                        label="商品類型"
                        onChange={(e) => setProductType(e.target.value)}
                        disabled={loading}
                      >
                        {productTypeOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="報值金額 (選填)"
                      type="number"
                      value={declaredValue}
                      onChange={(e) => setDeclaredValue(e.target.value)}
                      disabled={loading}
                      inputProps={{ min: 0 }}
                      helperText="保值貨物請填入金額"
                    />
                  </Grid>
                </>
              )}

              {/* Manual tracking number (when not using Hsinchu) */}
              {(!useHsinchu || carrier !== '新竹物流') && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label="追蹤號碼"
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value)}
                    disabled={loading}
                    placeholder="請輸入追蹤號碼"
                    helperText="此號碼將用於客戶查詢配送狀態"
                  />
                </Grid>
              )}

              {/* Estimated delivery date */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="預計送達日期"
                  type="date"
                  value={estimatedDeliveryDate}
                  onChange={(e) => setEstimatedDeliveryDate(e.target.value)}
                  disabled={loading}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  helperText="選填，預估的送達日期"
                />
              </Grid>

              {/* Delivery notes */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="配送備註"
                  multiline
                  rows={3}
                  value={deliveryNotes}
                  onChange={(e) => setDeliveryNotes(e.target.value)}
                  disabled={loading}
                  placeholder="配送相關備註（如：指定時間、特殊要求等）"
                />
              </Grid>
            </Grid>

            {/* Summary Display */}
            {((useHsinchu && carrier === '新竹物流') || (!useHsinchu && trackingNumber)) && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>物流商:</strong> {carrier}
                  <br />
                  {useHsinchu && carrier === '新竹物流' ? (
                    <>
                      <strong>出貨方式:</strong> 自動建立出貨單
                      <br />
                      <strong>重量:</strong> {weight} kg
                      <br />
                      <strong>商品類型:</strong> {productTypeOptions.find(p => p.value === productType)?.label}
                      {declaredValue && (
                        <>
                          <br />
                          <strong>報值金額:</strong> NT$ {declaredValue}
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <strong>追蹤號碼:</strong> {trackingNumber}
                    </>
                  )}
                  {estimatedDeliveryDate && (
                    <>
                      <br />
                      <strong>預計送達:</strong> {estimatedDeliveryDate}
                    </>
                  )}
                </Typography>
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
      {!hsinchuResult ? (
        /* Before creation */
        <>
          <Button 
            onClick={handleClose} 
            disabled={loading}
            color="inherit"
          >
            取消
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || (useHsinchu && carrier === '新竹物流' ? (!weight || !recipientPhone) : !trackingNumber.trim())}
            startIcon={loading ? <CircularProgress size={20} /> : <ShippingIcon />}
            color="warning"
            sx={{ minWidth: 120 }}
          >
            {loading ? '處理中...' : useHsinchu && carrier === '新竹物流' ? '建立出貨單' : '建立宅配'}
          </Button>
        </>
      ) : (
        /* After successful creation */
        <>
          <Button
            onClick={handlePrintLabel}
            variant="outlined"
            startIcon={<PrintIcon />}
            color="primary"
            sx={{ mr: 'auto' }}
          >
            重新列印標籤
          </Button>
          
          <Button
            onClick={handleClose}
            variant="contained"
            color="success"
            startIcon={<ShippingIcon />}
          >
            完成設定
          </Button>
        </>
      )}
    </DialogActions>
    </Dialog>
  );
};

export default HomeDeliveryModal;